import { useMemo } from 'react'
import { EntityMediaModal, type EntityMediaModalMessages } from '@/shared/media'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AssetHolding } from '@/modules/inventory/holdings'
import {
  deleteBatchAttachment,
  getBatchAttachmentUrl,
  getBatchAttachments,
  updateBatchAttachmentName,
  uploadBatchAttachment,
} from '@/modules/inventory/holdings/batches/batchAttachmentService'

interface BatchAttachmentModalProps {
  batch: AssetHolding
  onClose: () => void
}

const batchAttachmentModalService = {
  getMedia: getBatchAttachments,
  uploadMedia: uploadBatchAttachment,
  getMediaUrl: getBatchAttachmentUrl,
  updateMediaName: updateBatchAttachmentName,
  deleteMedia: deleteBatchAttachment,
}

export function BatchAttachmentModal({ batch, onClose }: BatchAttachmentModalProps) {
  const { t } = useTranslation()

  const messages = useMemo<EntityMediaModalMessages>(() => {
    const ta = (key: string, params?: Record<string, string>) =>
      t(`registrations.batches.attachments.${key}`, params)

    return {
      title: ta('title'),
      attachedTitle: (count) => ta('attachedTitle', { count: String(count) }),
      empty: ta('empty'),
      loading: ta('loading'),
      newNameLabel: ta('newNameLabel'),
      newNamePlaceholder: ta('newNamePlaceholder'),
      uploading: ta('uploading'),
      dropzone: ta('dropzone'),
      hint: ta('hint'),
      types: {
        image: ta('types.image'),
        video: ta('types.video'),
        pdf: ta('types.pdf'),
        document: ta('types.document'),
        spreadsheet: ta('types.spreadsheet'),
        other: ta('types.other'),
      },
      actions: {
        close: ta('actions.close'),
        confirm: ta('actions.confirm'),
        open: ta('actions.open'),
        editName: ta('actions.editName'),
        remove: ta('actions.remove'),
      },
      messages: {
        loadError: ta('messages.loadError'),
        uploadSuccess: ta('messages.uploadSuccess'),
        openError: ta('messages.openError'),
        deleteTitle: ta('messages.deleteTitle'),
        deleteConfirm: (name) => ta('messages.deleteConfirm', { name }),
        deleteSuccess: ta('messages.deleteSuccess'),
        renameSuccess: ta('messages.renameSuccess'),
      },
    }
  }, [t])

  return (
    <EntityMediaModal
      entityId={batch.id}
      unitId={batch.unit_id}
      service={batchAttachmentModalService}
      messages={messages}
      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
      onClose={onClose}
    />
  )
}
